/** Linked on arrival: O(1) lookup by id, walkable in either direction from `parentId`/`childIds`. */

/** A real node rather than a `null` sentinel, so every folder id stays a plain `string`. */
export const ROOT_FOLDER_ID = '00000000-0000-0000-0000-000000000000';

export type FolderNode = {
  id: string;
  name: string;
  /** `null` only for the root node. */
  parentId: string | null;
  /** Direct children, in the order the server returned them. */
  childIds: readonly string[];
};

export type FolderTree = {
  /**
   * By id. Typed as possibly-absent because an unknown id really does yield `undefined` at
   * runtime, which is what every lookup below guards for — without the `| undefined` the type
   * claims otherwise and those guards read as dead code.
   */
  nodes: Readonly<Record<string, FolderNode | undefined>>;
};

const EMPTY_NODES: readonly FolderNode[] = Object.freeze([]);
const EMPTY_IDS: readonly string[] = Object.freeze([]);

/** The tree before anything has loaded. Shared instance, so it is referentially stable. */
export const EMPTY_FOLDER_TREE: FolderTree = Object.freeze({
  nodes: Object.freeze({
    [ROOT_FOLDER_ID]: Object.freeze({
      id: ROOT_FOLDER_ID,
      name: 'Root',
      parentId: null,
      childIds: EMPTY_IDS,
    }),
  }),
});

export function getNode(tree: FolderTree, folderId: string): FolderNode | undefined {
  return tree.nodes[folderId];
}

/** Direct children of a folder, in server order. Unknown ids yield an empty list. */
export function getChildren(tree: FolderTree, folderId: string): readonly FolderNode[] {
  const node = tree.nodes[folderId];
  if (!node || node.childIds.length === 0) return EMPTY_NODES;
  return node.childIds.flatMap((id) => {
    const child = tree.nodes[id];
    return child ? [child] : [];
  });
}

/**
 * Root-to-leaf trail for a folder, root first and the folder itself last -- the breadcrumb bar
 * renders this directly. Defends against a malformed tree by refusing to loop forever.
 */
export function getPath(tree: FolderTree, folderId: string): readonly FolderNode[] {
  const path: FolderNode[] = [];
  const seen = new Set<string>();

  let current: FolderNode | undefined = tree.nodes[folderId];
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    path.unshift(current);
    current = current.parentId === null ? undefined : tree.nodes[current.parentId];
  }

  return path;
}

/**
 * Every descendant id of a folder, at any depth, excluding the folder itself. Used to scope
 * assets to a folder subtree and to stop a folder being dropped inside its own subtree.
 */
export function getDescendantIds(tree: FolderTree, folderId: string): readonly string[] {
  const node = tree.nodes[folderId];
  if (!node) return EMPTY_IDS;

  const descendants: string[] = [];
  const queue = [...node.childIds];
  const seen = new Set<string>([folderId]);

  while (queue.length > 0) {
    const id = queue.shift() as string;
    if (seen.has(id)) continue;
    seen.add(id);

    const child = tree.nodes[id];
    if (!child) continue;

    descendants.push(id);
    queue.push(...child.childIds);
  }

  return descendants;
}

/**
 * Builds the normalised tree from a flat parent-pointer list -- what a real backend would do
 * before serialising. Folders whose `parentId` is `null` are attached to the root node.
 */
export function buildFolderTree(folders: readonly { id: string; name: string; parentId: string | null }[]): FolderTree {
  // Possibly-absent for the same reason as `FolderTree.nodes`: a folder can name a parent that
  // is not in the list, which the guard below handles.
  const nodes: Record<string, FolderNode | undefined> = {
    [ROOT_FOLDER_ID]: { id: ROOT_FOLDER_ID, name: 'Root', parentId: null, childIds: [] },
  };

  for (const folder of folders) {
    nodes[folder.id] = {
      id: folder.id,
      name: folder.name,
      parentId: folder.parentId ?? ROOT_FOLDER_ID,
      childIds: [],
    };
  }

  for (const folder of folders) {
    const parentId = folder.parentId ?? ROOT_FOLDER_ID;
    const parent = nodes[parentId];
    if (parent) {
      parent.childIds = [...parent.childIds, folder.id];
    }
  }

  return { nodes };
}

/**
 * The folder path to draw under an asset's name, trimmed against the folder being viewed.
 *
 * Every row in a folder's view is somewhere under that folder, so repeating that folder on each
 * one says nothing: viewing `A`, a file in `A -> B -> C` reads as `B -> C`, and one sitting
 * directly in `A` shows no path at all rather than an empty line.
 *
 * From the root there is no shared prefix to drop, so the whole path is shown. An asset from
 * outside the folder being viewed keeps its full path too -- nothing is shared, so there is
 * nothing to trim, and the full path is what locates it.
 *
 * `isFromRoot` says which of those two happened, so the caller can label the full path with the
 * root's name and leave a trimmed one unlabelled.
 */
export function relativeFolderPath(
  tree: FolderTree,
  folderId: string | undefined,
  viewedFolderId: string
): { folders: readonly FolderNode[]; isFromRoot: boolean } {
  if (folderId === undefined) return { folders: [], isFromRoot: false };

  const path = getPath(tree, folderId).filter((folder) => folder.id !== ROOT_FOLDER_ID);
  if (viewedFolderId === ROOT_FOLDER_ID) {
    return { folders: path, isFromRoot: true };
  }

  const viewedIndex = path.findIndex((folder) => folder.id === viewedFolderId);
  return viewedIndex === -1
    ? { folders: path, isFromRoot: true }
    : { folders: path.slice(viewedIndex + 1), isFromRoot: false };
}
